import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  apiBase: string;
  emailToken: string;
  onVerified: (phone: string, phoneToken: string) => void;
};

const RESEND_COOLDOWN = 60;

export default function PreRegPhoneVerify({ apiBase, emailToken, onVerified }: Props) {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState<'enter-phone' | 'enter-code'>('enter-phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const requestCode = async () => {
    setError('');
    setInfo('');
    if (!phone || phone.length < 8) {
      setError(t('preReg.invalidPhone', 'Numéro invalide'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/auth/pre-register/phone/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), email_token: emailToken, lang: i18n.language === 'ar' ? 'ar' : 'fr' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t('preReg.sendError', "Erreur d'envoi"));
        return;
      }
      setPhase('enter-code');
      setInfo(t('preReg.phoneCodeSent', 'Code SMS envoyé au {{phone}}.', { phone }));
      setResendCooldown(RESEND_COOLDOWN);
    } catch {
      setError(t('preReg.sendError', "Erreur d'envoi"));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    if (code.length !== 6) {
      setError(t('preReg.invalidCodeLength', 'Le code doit faire 6 chiffres.'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/auth/pre-register/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), code, email_token: emailToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.phone_token) {
        setError(data.detail || t('preReg.invalidCode', 'Code invalide.'));
        return;
      }
      onVerified(phone.trim(), data.phone_token);
    } catch {
      setError(t('preReg.invalidCode', 'Code invalide.'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = (action: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="prereg-step">
      <h4 className="mb-3">{t('preReg.phoneTitle', 'Vérifiez votre téléphone')}</h4>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}
      {info && !error && (
        <div className="alert alert-info" role="alert">
          <i className="bi bi-info-circle me-2"></i>{info}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">{t('common.phone', 'Téléphone')} <span className="text-danger">*</span></label>
        <div className="input-group">
          <span className="input-group-text"><i className="bi bi-phone"></i></span>
          <input
            type="tel"
            className="form-control"
            placeholder="0555 12 34 56"
            value={phone}
            disabled={phase === 'enter-code'}
            onChange={(e) => setPhone(e.target.value)}
            onKeyPress={handleEnter(requestCode)}
          />
        </div>
        <small className="text-muted">
          {t('preReg.phoneHelp', 'Format : 0555... ou +213555... — un SMS sera envoyé pour vérification.')}
        </small>
      </div>

      {phase === 'enter-phone' && (
        <button onClick={requestCode} className="btn btn-primary w-100" disabled={loading}>
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.loading', 'Chargement...')}</>
            : <><i className="bi bi-chat-dots me-2"></i>{t('preReg.sendSms', 'Envoyer le SMS')}</>}
        </button>
      )}

      {phase === 'enter-code' && (
        <>
          <div className="mb-3">
            <label className="form-label">{t('preReg.smsCodeLabel', 'Code reçu par SMS')}</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="form-control text-center"
              style={{ fontSize: '24px', letterSpacing: '8px' }}
              placeholder="——————"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyPress={handleEnter(verifyCode)}
              autoFocus
            />
          </div>

          <button onClick={verifyCode} className="btn btn-primary w-100 mb-2" disabled={loading}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.loading', 'Chargement...')}</>
              : <><i className="bi bi-check2-circle me-2"></i>{t('preReg.verify', 'Vérifier')}</>}
          </button>

          <button
            onClick={requestCode}
            className="btn btn-link w-100"
            disabled={loading || resendCooldown > 0}
          >
            {resendCooldown > 0
              ? t('preReg.resendIn', 'Renvoyer dans {{s}}s', { s: resendCooldown })
              : t('preReg.resend', 'Renvoyer le code')}
          </button>
        </>
      )}
    </div>
  );
}
